// Sentry SDK sidecar — see https://docs.sentry.io/platforms/javascript/guides/react/.
//
// Loaded synchronously as the very first import in main.tsx so any error
// thrown during app boot (before React renders) is captured. The old lazy
// initSentry() pattern missed those ~100–300ms.
//
// release is the short git SHA, injected at build time via vite's define
// (see vite.config.ts) — keeps the bundle free of process.env churn.
//
// tracePropagationTargets bridges the gap between frontend Sentry and the
// /api/* serverside Sentry so a single trace covers the whole request.

import * as Sentry from '@sentry/react';

declare const __APP_VERSION__: string;

const dsn = import.meta.env.VITE_SENTRY_DSN;

if (dsn && import.meta.env.PROD) {
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: __APP_VERSION__,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: 0.1,
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/app(\.(dev|staging))?\.krackencode\.com/,
      /^https:\/\/personas(\.(dev|staging))?\.krackencode\.com/,
      /^https:\/\/studio(\.(dev|staging))?\.krackencode\.com/,
      /^\/api\//,
    ],
    // Don't record idle sessions to keep storage cost predictable; do
    // record around every error so we can see what the user was doing.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    // Filter browser-extension noise. The CSP eval-block fires when an
    // extension (1Password's "Backpack" bridge, password managers,
    // ad blockers) tries to inject string-based code; Sentry's global
    // handler catches the resulting EvalError and reports it as if it
    // were ours. It's not — our code never calls eval().
    ignoreErrors: [
      /Refused to evaluate a string as JavaScript/,
      /backpack.*secure-background/i,
    ],
    denyUrls: [
      /chrome-extension:\/\//,
      /moz-extension:\/\//,
      /safari-extension:\/\//,
    ],
    beforeSend(event) {
      if (event.user) {
        delete event.user.ip_address;
      }
      return event;
    },
  });
}

export { Sentry };
